class HeaderMobileDrawer extends HTMLElement {
  #toggle = null;
  #panel = null;
  #openLabel = '';
  #closeLabel = '';
  #controller = null;
  #closeTimeout = null;

  connectedCallback() {
    this.#toggle = this.querySelector('[data-mobile-menu-toggle]');
    this.#panel = this.querySelector('[data-mobile-menu-panel]');

    if (!this.#toggle || !this.#panel) {
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#openLabel = this.#toggle.getAttribute('aria-label') || '';
    this.#closeLabel = this.#toggle.dataset.closeLabel || this.#openLabel;

    this.#toggle.addEventListener('click', () => {
      const isOpen = this.#toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.#close();
      } else {
        this.#open();
      }
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.#close();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#clearCloseTimeout();
    this.#unlockScroll();
  }

  #open() {
    this.#clearCloseTimeout();
    this.#toggle.setAttribute('aria-expanded', 'true');
    this.#toggle.setAttribute('aria-label', this.#closeLabel);
    this.#panel.hidden = false;
    this.#lockScroll();

    const openIcon = this.#toggle.querySelector('.header-erodus__icon-open');
    const closeIcon = this.#toggle.querySelector('.header-erodus__icon-close');

    if (openIcon) {
      openIcon.hidden = true;
    }

    if (closeIcon) {
      closeIcon.hidden = false;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('header-erodus__mobile--open');

        const firstLink = this.#panel.querySelector('.header-erodus__mobile-nav-link');
        if (firstLink) {
          firstLink.focus();
        }
      });
    });
  }

  #close() {
    if (!this.#panel || this.#panel.hidden) {
      return;
    }

    this.#clearCloseTimeout();
    this.#toggle.setAttribute('aria-expanded', 'false');
    this.#toggle.setAttribute('aria-label', this.#openLabel);
    this.classList.remove('header-erodus__mobile--open');

    const openIcon = this.#toggle.querySelector('.header-erodus__icon-open');
    const closeIcon = this.#toggle.querySelector('.header-erodus__icon-close');

    if (openIcon) {
      openIcon.hidden = false;
    }

    if (closeIcon) {
      closeIcon.hidden = true;
    }

    const finishClose = () => {
      this.#panel.hidden = true;
      this.#unlockScroll();
      this.#toggle?.focus();
    };

    if (this.#prefersReducedMotion()) {
      finishClose();
      return;
    }

    const handleTransitionEnd = (event) => {
      if (event.target !== this.#panel || event.propertyName !== 'opacity') {
        return;
      }

      this.#panel.removeEventListener('transitionend', handleTransitionEnd);
      this.#clearCloseTimeout();
      finishClose();
    };

    this.#panel.addEventListener('transitionend', handleTransitionEnd);
    this.#closeTimeout = window.setTimeout(() => {
      this.#panel.removeEventListener('transitionend', handleTransitionEnd);
      this.#clearCloseTimeout();
      finishClose();
    }, this.#getDrawerDurationMs() + 50);
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #getDrawerDurationMs() {
    const duration = getComputedStyle(this.#panel).getPropertyValue('--header-erodus-mobile-drawer-duration').trim();
    const value = Number.parseFloat(duration);

    if (Number.isNaN(value)) {
      return 250;
    }

    if (duration.endsWith('ms')) {
      return value;
    }

    if (duration.endsWith('s')) {
      return value * 1000;
    }

    return value;
  }

  #clearCloseTimeout() {
    if (this.#closeTimeout) {
      window.clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }
  }

  #lockScroll() {
    document.documentElement.classList.add('header-erodus--menu-open');
  }

  #unlockScroll() {
    document.documentElement.classList.remove('header-erodus--menu-open');
  }

  isMenuOpen() {
    return this.classList.contains('header-erodus__mobile--open');
  }
}

if (!customElements.get('header-mobile-drawer')) {
  customElements.define('header-mobile-drawer', HeaderMobileDrawer);
}

class HeaderErodusSticky {
  static #heightVar = '--header-sticky-height';

  #shell = null;
  #resizeObserver = null;
  #controller = null;

  constructor(section) {
    this.#shell = section.querySelector('.header-erodus__shell');
  }

  connect() {
    if (!this.#shell) {
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#syncLayoutHeight();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#syncLayoutHeight();
    });
    this.#resizeObserver.observe(this.#shell);

    window.addEventListener('scroll', () => {
      this.#syncLayoutHeight();
    }, { passive: true, signal });
  }

  disconnect() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#controller?.abort();
    this.#controller = null;
    document.documentElement.style.removeProperty(HeaderErodusSticky.#heightVar);
  }

  #syncLayoutHeight() {
    const visibleHeight = Math.max(0, this.#shell.getBoundingClientRect().bottom);

    document.documentElement.style.setProperty(
      HeaderErodusSticky.#heightVar,
      `${visibleHeight}px`,
    );
  }
}

const headerSection = document.querySelector('.header-erodus-section:has(.header-erodus--sticky)');

if (headerSection) {
  const stickyHeader = new HeaderErodusSticky(headerSection);
  stickyHeader.connect();
}

if (!document.documentElement.classList.contains('promo-code-copy-bound')) {
  document.documentElement.classList.add('promo-code-copy-bound');
  const promoRoot = document.querySelector('.header-erodus__announcement');
  const copiedFeedbackTimeouts = new WeakMap();

  const showCopiedFeedback = (button) => {
    const copiedLabel = button.dataset.copiedLabel;
    const originalAriaLabel = button.getAttribute('aria-label');
    const copiedElement = button.querySelector('.header-erodus__promo-copied');

    if (!copiedLabel) {
      return;
    }

    const existingTimeout = copiedFeedbackTimeouts.get(button);

    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    button.classList.add('header-erodus__promo-chip--copied');
    button.setAttribute('aria-label', copiedLabel);

    if (copiedElement) {
      copiedElement.setAttribute('aria-hidden', 'false');
    }

    const timeoutId = window.setTimeout(() => {
      button.classList.remove('header-erodus__promo-chip--copied');

      if (originalAriaLabel) {
        button.setAttribute('aria-label', originalAriaLabel);
      }

      if (copiedElement) {
        copiedElement.setAttribute('aria-hidden', 'true');
      }

      copiedFeedbackTimeouts.delete(button);
    }, 2000);

    copiedFeedbackTimeouts.set(button, timeoutId);
  };

  if (promoRoot) {
    promoRoot.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-copy-promo]');

      if (!button) {
        return;
      }

      const code = button.getAttribute('data-copy-promo');

      if (!code) {
        return;
      }

      try {
        await navigator.clipboard.writeText(code);
        showCopiedFeedback(button);
      } catch {
        return;
      }
    });
  }
}
